import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Box, Typography, TextField, Button, Alert, Stack, Checkbox, FormControlLabel, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import TidioChat from '../components/TidioChat';
import { sanitizeInput, validateEmail } from '../lib/validation';
import OnemoveIcon from '../../onemove-icon.png'; 

export default function Login() {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      setSuccess('Email verified successfully! Please login.');
    }
  }, [location]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const sanitizedInput = sanitizeInput(emailOrUsername);
    const sanitizedPassword = sanitizeInput(password);

    const isEmail = validateEmail(sanitizedInput);
    
    let email = '';
    if (isEmail) {
        email = sanitizedInput;
    } else {
        const { data, error: fetchError } = await supabase
            .from('users')
            .select('email')
            .eq('username', sanitizedInput)
            .single();

        if (fetchError || !data) {
            setError('Invalid username or password');
            setLoading(false);
            return;
        }
        email = data.email;
    }

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ 
        email: email, 
        password: sanitizedPassword 
      });

      if (signInError) throw signInError;

      if (!data.user?.email_confirmed_at) {
        setError('Please verify your email before logging in');
        await supabase.auth.signOut();
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    // ... (logic for resending verification)
  };

  return (
    <>
      <TidioChat />
      <Box
        sx={{
          width: { xs: '90%', sm: 400 },
          p: 4,
          borderRadius: '16px',
          bgcolor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <img src={OnemoveIcon} alt="OneMove Logo" style={{ width: 60, height: 60, marginBottom: '16px' }} />

          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: '#333' }}>
            Login to OneMove
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
              {error.includes('verify your email') && (
                <Button color="inherit" onClick={handleResendVerification} sx={{ ml: 2, textTransform: 'none' }}>
                  Resend
                </Button>
              )}
            </Alert>
          )}
          {success && <Alert severity="success" sx={{ width: '100%' }}>{success}</Alert>}

          <form onSubmit={handleLogin} style={{ width: '100%' }}>
            <Stack spacing={2}>
              <TextField
                label="Username or Email"
                variant="outlined"
                fullWidth
                value={emailOrUsername}
                onChange={e => setEmailOrUsername(e.target.value)}
                required
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={<Checkbox defaultChecked sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />}
                label="Stay Logged In"
              />
              <Button
                type="submit"
                variant="contained"
                color="error"
                fullWidth
                disabled={loading}
                sx={{ py: 1.5, fontWeight: 600, textTransform: 'none', borderRadius: '8px' }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </Stack>
          </form>

          <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', mt: 2 }}>
            <Button
              component={Link}
              to="/signup"
              sx={{ textTransform: 'none', color: '#d32f2f' }}
            >
              Register Account
            </Button>
            <Button
              component={Link}
              to="/forgot-password"
              sx={{ textTransform: 'none', color: '#d32f2f' }}
            >
              Forgot Password?
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>
  );
} 